import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import type {
  Capability,
  ScopeCapabilitiesResponse,
  SetScopeCapabilityRequest,
} from '@/api/pending'
import { useAccess } from '@/access/useAccess'
import {
  Button,
  Card,
  EmptyRow,
  PageHeader,
  Row,
  SectionTitle,
  Spinner,
  Toggle,
  useToast,
} from '@/ui'
import { scopeCapabilitiesQuery, useSaveScopeCapabilities } from './api'
import {
  CAPABILITY_HINT,
  GRANTABLE_LABEL,
  S,
  SCOPE_DEFAULT,
  SCOPE_DESCRIPTION,
  SCOPE_LABEL,
} from './strings'
import styles from './PermissionsView.module.css'

type ConfigurableScope = 'reviewer' | 'doorman'
const SCOPES: ConfigurableScope[] = ['reviewer', 'doorman']
type Draft = Record<ConfigurableScope, Capability[]>

/** Only what the server calls grantable, and never `overview`, `balances` or `team`. */
const grantableOf = (data: ScopeCapabilitiesResponse) =>
  data.grantable.filter((c) => c in GRANTABLE_LABEL)

export function PermissionsView() {
  const { condoId } = useAccess()
  const toast = useToast()
  const navigate = useNavigate()
  const { data, isPending, isError } = useQuery(scopeCapabilitiesQuery(condoId))
  const save = useSaveScopeCapabilities(condoId)
  const [draft, setDraft] = useState<Draft | null>(null)

  const current: Draft | null = data ? (draft ?? data.effective) : null
  const changes: SetScopeCapabilityRequest[] =
    data && draft
      ? SCOPES.flatMap((scope) =>
          grantableOf(data).flatMap((capability) => {
            const was = data.effective[scope].includes(capability)
            const now = draft[scope].includes(capability)
            return was === now ? [] : [{ scope, capability, enabled: now }]
          }),
        )
      : []
  const dirty = changes.length > 0

  function toggle(scope: ConfigurableScope, capability: Capability, on: boolean) {
    if (!data || !current) return
    const base = draft ?? data.effective
    const next = on ? [...base[scope], capability] : base[scope].filter((c) => c !== capability)
    setDraft({ ...base, [scope]: next })
  }

  function onSave() {
    save.mutate(changes, {
      onSuccess: () => {
        setDraft(null)
        toast(S.permsSaved)
      },
    })
  }

  return (
    <>
      <PageHeader
        title={S.permsTitle}
        subtitle={S.permsSubtitle}
        actions={
          <Button
            variant="ghost"
            icon="arrow_back"
            onClick={() => void navigate(`/c/${condoId}/staff`)}
          >
            {S.permsBack}
          </Button>
        }
      />
      {isPending && (
        <div className={styles.loading}>
          <Spinner />
        </div>
      )}
      {isError && (
        <Card pad={0}>
          <EmptyRow
            icon="error"
            title={S.permsError}
            description="Recarregue a página para tentar de novo."
          />
        </Card>
      )}
      {data && current && (
        <>
          <div className={styles.grid}>
            {SCOPES.map((scope) => (
              <Card key={scope} pad={0}>
                <div className={styles.cardHead}>
                  <SectionTitle>{SCOPE_LABEL[scope]}</SectionTitle>
                  <div className={styles.cardDescription}>{SCOPE_DESCRIPTION[scope]}</div>
                </div>
                {grantableOf(data).map((capability) => {
                  const locked = SCOPE_DEFAULT[scope] === capability
                  const label = GRANTABLE_LABEL[capability]!
                  return (
                    <Row key={capability}>
                      <div className={styles.capText}>
                        <div className={styles.capLabel}>{label}</div>
                        <div className={styles.capHint}>
                          {locked ? S.permsLocked : CAPABILITY_HINT[capability]}
                        </div>
                      </div>
                      <Toggle
                        aria-label={`${label}: ${SCOPE_LABEL[scope]}`}
                        checked={locked || current[scope].includes(capability)}
                        disabled={locked}
                        onChange={(on) => toggle(scope, capability, on)}
                      />
                    </Row>
                  )
                })}
              </Card>
            ))}
          </div>
          <p className={styles.live}>{S.permsLive}</p>
          <div className={styles.footer}>
            {save.isError && (
              <span role="alert" className={styles.error}>
                {S.permsError}
              </span>
            )}
            <Button disabled={!dirty} loading={save.isPending} icon="check" onClick={onSave}>
              {S.permsSave}
            </Button>
          </div>
        </>
      )}
    </>
  )
}
